import { useMemo } from 'react';

import { Message } from '@/services/types/artifact-types';

import { MessageItem } from './MessageItem';

interface MessageListProps {
  messages: Message[];
  searchQuery: string;
}

export const MessageList = ({ messages, searchQuery }: MessageListProps) => {
  // Group messages by date for better organization
  const groupedMessages = useMemo(() => {
    const groups: Record<string, Message[]> = {};

    messages.forEach((message) => {
      const date = new Date(message.created_at).toDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(message);
    });

    return groups;
  }, [messages]);

  const formatDateHeader = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    }
    if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (messages.length === 0) {
    return (
      <div className="flex items-center justify-center h-full p-8 text-center">
        <div className="max-w-sm">
          <p className="text-gray-500 text-sm">
            {searchQuery
              ? 'No messages found matching your search criteria.'
              : 'No messages in this conversation.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-y-auto h-full">
      <div className="p-4 space-y-6">
        {Object.entries(groupedMessages)
          .sort(([a], [b]) => new Date(b).getTime() - new Date(a).getTime())
          .map(([date, dateMessages]) => (
            <div key={date} className="space-y-3">
              {/* Date Header (sticky) */}
              <div className="flex items-center justify-center sticky top-0 z-10 py-1">
                <div className="bg-gray-100 px-3 py-1 rounded-full shadow-sm">
                  <span className="text-xs font-medium text-gray-600">
                    {formatDateHeader(date)}
                  </span>
                </div>
              </div>

              {/* Messages for this date */}
              <div className="space-y-3">
                {dateMessages
                  .sort(
                    (a, b) =>
                      new Date(a.created_at).getTime() -
                      new Date(b.created_at).getTime(),
                  )
                  .map((message) => (
                    <MessageItem
                      key={message.id}
                      message={message}
                      searchQuery={searchQuery}
                    />
                  ))}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};
